import React, { useState, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Briefcase, Loader2, ArrowLeft, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const safeNext = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
};

const ProfessionalLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return safeNext(params.get('next'));
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/professionals/login`, formData);
      localStorage.setItem('professionalToken', data.token);
      localStorage.setItem('professionalId', data.professional.id);
      localStorage.setItem('professionalName', data.professional.fullName);
      localStorage.setItem('professionalEmail', data.professional.email);
      toast.success('Login successful!');
      navigate(nextPath || '/professional/directory');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
      <Card className="w-full max-w-md p-8 shadow-floating">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-serif font-bold text-2xl text-primary mb-2">
            Professional Login
          </h1>
          <p className="text-muted-foreground text-sm">
            Login to access the Professional Directory
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="••••••••"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Logging in...</>
            ) : (
              'Login'
            )}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Not registered yet?
          </p>
          <Link to="/professional/register">
            <Button variant="outline" className="w-full">
              <UserPlus size={16} className="mr-2" /> Register as Professional
            </Button>
          </Link>
        </div>

        <div className="mt-4 text-center">
          <Link to="/professionals" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center">
            <ArrowLeft size={14} className="mr-1" /> Back to Professionals
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ProfessionalLoginPage;
